<template>
  <div class="car-request-wrapper">
    <!-- Кнопка для открытия модалки -->
    <button @click="openModal" class="open-modal-button">
      <span>Request a Car</span>
    </button>

    <!-- Модальное окно -->
    <transition name="modal-fade">
      <div v-if="isModalOpen" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <button class="close-button" @click="closeModal">
            <i class="bi bi-x-lg"></i>
          </button>

          <div class="car-request-form">
            <div class="form-header">
              <h2>Request a Car</h2>
              <p class="subtitle">
                Can't find what you're looking for? Let us know and we'll help
                you find it!
              </p>
            </div>

            <template v-if="!isSuccess">
              <form @submit.prevent="submitRequest" novalidate>
                <div class="form-grid">
                  <div class="form-group" :class="{ 'has-error': errors.name }">
                    <label for="name"
                      >Full Name <span class="required">*</span></label
                    >
                    <input
                      type="text"
                      id="name"
                      v-model.trim="formData.name"
                      @input="validateName"
                      placeholder="John Doe"
                      :disabled="submitting"
                      required
                    />
                    <span v-if="errors.name" class="error-text">{{
                      errors.name
                    }}</span>
                  </div>

                  <div
                    class="form-group"
                    :class="{ 'has-error': errors.email }"
                  >
                    <label for="email"
                      >Email Address <span class="required">*</span></label
                    >
                    <input
                      type="email"
                      id="email"
                      v-model.trim="formData.email"
                      @input="validateEmail"
                      placeholder="john.doe@example.com"
                      :disabled="submitting"
                      required
                    />
                    <span v-if="errors.email" class="error-text">{{
                      errors.email
                    }}</span>
                  </div>

                  <div
                    class="form-group"
                    :class="{ 'has-error': errors.phone }"
                  >
                    <label for="phone"
                      >Phone Number <span class="required">*</span></label
                    >
                    <input
                      type="tel"
                      id="phone"
                      v-model="formData.phone"
                      v-mask="'(###) ###-####'"
                      @input="validatePhone"
                      placeholder="(555) 123-4567"
                      :disabled="submitting"
                      required
                    />
                    <span v-if="errors.phone" class="error-text">{{
                      errors.phone
                    }}</span>
                  </div>

                  <div
                    class="form-group full-width"
                    :class="{ 'has-error': errors.carDetails }"
                  >
                    <label for="carDetails"
                      >What car are you looking for?
                      <span class="required">*</span></label
                    >
                    <textarea
                      id="carDetails"
                      v-model.trim="formData.carDetails"
                      @input="validateCarDetails"
                      placeholder="Example: 2022 Toyota Camry SE, black or white, under 30k miles"
                      rows="4"
                      :disabled="submitting"
                      required
                    ></textarea>
                    <span v-if="errors.carDetails" class="error-text">{{
                      errors.carDetails
                    }}</span>
                  </div>
                </div>

                <div
                  class="form-consent"
                  :class="{ 'has-error': errors.consent }"
                >
                  <label>
                    <input
                      type="checkbox"
                      v-model="formData.consent"
                      @change="validateConsent"
                      :disabled="submitting"
                      required
                    />
                    I agree to the processing of data in accordance with the
                    <router-link to="/privacy" class="privacy-link"
                      >Privacy Policy</router-link
                    >
                  </label>
                  <span v-if="errors.consent" class="error-text">{{
                    errors.consent
                  }}</span>
                </div>

                <div v-if="submissionError" class="error-message">
                  <i class="bi bi-exclamation-triangle"></i>
                  {{ submissionError }}
                </div>

                <button
                  type="submit"
                  class="submit-button"
                  :disabled="submitting || hasErrors"
                >
                  <i class="bi bi-send" v-if="!submitting"></i>
                  <i class="bi bi-hourglass-split spinning" v-else></i>
                  <span>{{
                    submitting ? "Sending..." : "Submit Request"
                  }}</span>
                </button>
              </form>
            </template>

            <template v-else>
              <div class="success-section">
                <div class="success-icon">
                  <i class="bi bi-check-circle"></i>
                </div>
                <h3>Request Received!</h3>
                <p>
                  Thank you for your interest! We've received your request and
                  our team will contact you soon with available options.
                </p>
                <button class="new-request-button" @click="resetForm">
                  <i class="bi bi-plus-circle"></i>
                  <span>Submit Another Request</span>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mask } from "vue-the-mask";
import axios from "axios";
import API_CONFIG from "@/config/api.config.js";

export default {
  name: "CarRequestModal",
  directives: {
    mask,
  },
  data() {
    return {
      isModalOpen: false,
      formData: {
        name: "",
        email: "",
        phone: "",
        carDetails: "",
        consent: false,
      },
      errors: {
        name: "",
        email: "",
        phone: "",
        carDetails: "",
        consent: "",
      },
      submissionError: null,
      submitting: false,
      isSuccess: false,
      baseUrl: API_CONFIG.baseUrl,
    };
  },
  computed: {
    hasErrors() {
      return Object.values(this.errors).some((error) => error !== "");
    },
  },
  methods: {
    openModal() {
      this.isModalOpen = true;
      document.body.style.overflow = "hidden";
    },
    closeModal() {
      this.isModalOpen = false;
      document.body.style.overflow = "";
      if (this.isSuccess) {
        this.resetForm();
      }
    },
    validateName() {
      const name = this.formData.name;
      if (!name) {
        this.errors.name = "Full name is required";
      } else if (name.length < 2) {
        this.errors.name = "Name must be at least 2 characters";
      } else if (!/^[a-zA-Z\s-]+$/.test(name)) {
        this.errors.name = "Name can only contain letters, spaces, and hyphens";
      } else {
        this.errors.name = "";
      }
    },
    validateEmail() {
      const email = this.formData.email;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email) {
        this.errors.email = "Email is required";
      } else if (!emailRegex.test(email)) {
        this.errors.email = "Please enter a valid email address";
      } else {
        this.errors.email = "";
      }
    },
    validatePhone() {
      const phone = this.formData.phone.replace(/\D/g, "");
      if (!phone) {
        this.errors.phone = "Phone number is required";
      } else if (phone.length !== 10) {
        this.errors.phone = "Please enter a valid 10-digit US phone number";
      } else {
        this.errors.phone = "";
      }
    },
    validateCarDetails() {
      const details = this.formData.carDetails;
      if (!details) {
        this.errors.carDetails = "Please describe the car you're looking for";
      } else if (details.length < 10) {
        this.errors.carDetails =
          "Please provide more details (at least 10 characters)";
      } else {
        this.errors.carDetails = "";
      }
    },
    validateConsent() {
      if (!this.formData.consent) {
        this.errors.consent = "You must agree to the privacy policy";
      } else {
        this.errors.consent = "";
      }
    },
    validateForm() {
      this.validateName();
      this.validateEmail();
      this.validatePhone();
      this.validateCarDetails();
      this.validateConsent();
      return !this.hasErrors;
    },
    async submitRequest() {
      if (!this.validateForm()) {
        this.submissionError = "Please fill in all required fields correctly";
        return;
      }

      this.submitting = true;
      this.submissionError = null;

      try {
        const requestData = {
          name: this.formData.name,
          email: this.formData.email,
          phone: this.formData.phone.replace(/\D/g, ""),
          car_details: this.formData.carDetails,
        };

        const response = await axios.post(
          `${this.baseUrl}/api/car_request_unavailable`,
          requestData,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        if (response.data.success) {
          this.isSuccess = true;
          this.$emit("request-submitted", requestData);
        } else {
          throw new Error(response.data.error || "Submission failed");
        }
      } catch (error) {
        console.error("Submission error:", error);
        this.submissionError =
          "Failed to submit request. Please try again later.";
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.formData = {
        name: "",
        email: "",
        phone: "",
        carDetails: "",
        consent: false,
      };
      this.errors = {
        name: "",
        email: "",
        phone: "",
        carDetails: "",
        consent: "",
      };
      this.submissionError = null;
      this.isSuccess = false;
      this.submitting = false;
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style lang="scss" scoped>
.car-request-wrapper {
  .open-modal-button {
    background: linear-gradient(135deg, #ff6b00, #ff9f43);
    color: white;
    border: none;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 107, 0, 0.2);

    i {
      font-size: 1.3rem;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(255, 107, 0, 0.3);
    }

    &:active {
      transform: translateY(0);
    }
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    padding: 1rem;
    overflow-y: auto;
  }

  .modal-container {
    position: relative;
    width: 100%;
    max-width: 750px;
    max-height: 90vh;
    overflow-y: auto;
    background: white;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ff6b00;
      border-radius: 10px;

      &:hover {
        background: #e66000;
      }
    }
  }

  .close-button {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: #f5f5f5;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;

    i {
      font-size: 1.2rem;
      color: #666;
    }

    &:hover {
      background: #ff6b00;
      transform: rotate(90deg);

      i {
        color: white;
      }
    }
  }

  .car-request-form {
    padding: 2.5rem;

    .form-header {
      text-align: center;
      margin-bottom: 2.5rem;
      padding-right: 2rem;

      i {
        font-size: 3.5rem;
        color: #ff6b00;
        margin-bottom: 1rem;
        display: block;
      }

      h2 {
        font-size: 2.2rem;
        font-weight: 700;
        color: #1a3c5e;
        margin-bottom: 0.5rem;
      }

      .subtitle {
        font-size: 1rem;
        color: #666;
        line-height: 1.5;
      }
    }

    .form-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.5rem;
      margin-bottom: 1.5rem;

      .form-group {
        position: relative;

        &.full-width {
          grid-column: 1 / -1;
        }

        &.has-error {
          input,
          textarea {
            border-color: #ff4444;
            box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
          }
        }

        label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #1a3c5e;
          font-size: 0.95rem;
        }

        .required {
          color: #ff4444;
          font-size: 0.9em;
        }

        input,
        textarea {
          width: 100%;
          padding: 0.9rem;
          border: 2px solid #e0e0e0;
          border-radius: 10px;
          font-size: 1rem;
          transition: all 0.3s ease;
          font-family: inherit;

          &::placeholder {
            color: #aaa;
          }

          &:focus {
            outline: none;
            border-color: #ff6b00;
            box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
          }

          &:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
          }
        }

        textarea {
          resize: vertical;
          min-height: 100px;
        }

        .error-text {
          color: #ff4444;
          font-size: 0.8rem;
          margin-top: 0.3rem;
          display: block;
          position: absolute;
          bottom: -1.8rem;
          left: 0;
        }
      }
    }

    .form-consent {
      margin-bottom: 2rem;

      &.has-error {
        label {
          color: #ff4444;
        }
      }

      label {
        display: flex;
        align-items: flex-start;
        font-size: 0.9rem;
        color: #666;
        cursor: pointer;
        line-height: 1.5;

        input[type="checkbox"] {
          margin-right: 0.6rem;
          margin-top: 0.2rem;
          accent-color: #ff6b00;
          width: 18px;
          height: 18px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .privacy-link {
          color: #ff6b00;
          text-decoration: none;
          font-weight: 500;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      .error-text {
        color: #ff4444;
        font-size: 0.8rem;
        margin-top: 0.3rem;
        display: block;
        margin-left: 1.5rem;
      }
    }

    .error-message {
      background: linear-gradient(135deg, #ffe6e6, #ffcccc);
      color: #cc0000;
      padding: 1rem 1.2rem;
      border-radius: 10px;
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      gap: 0.7rem;
      font-size: 0.95rem;
      font-weight: 500;

      i {
        font-size: 1.2rem;
      }
    }

    .submit-button {
      width: 100%;
      background: linear-gradient(135deg, #ff6b00, #ff9f43);
      color: white;
      border: none;
      padding: 1.1rem 2rem;
      font-size: 1.1rem;
      font-weight: 600;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.6rem;
      box-shadow: 0 4px 15px rgba(255, 107, 0, 0.2);

      i {
        font-size: 1.2rem;
      }

      .spinning {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(255, 107, 0, 0.3);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }

      &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
      }
    }

    .success-section {
      text-align: center;
      padding: 2rem 0;
      animation: fadeInScale 0.5s ease-out;

      @keyframes fadeInScale {
        from {
          opacity: 0;
          transform: scale(0.9);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      .success-icon {
        margin-bottom: 1.5rem;

        i {
          font-size: 5rem;
          color: #28a745;
          animation: checkmark 0.6s ease-out;
        }

        @keyframes checkmark {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      }

      h3 {
        font-size: 2rem;
        color: #1a3c5e;
        margin-bottom: 1rem;
        font-weight: 700;
      }

      p {
        color: #666;
        font-size: 1.1rem;
        line-height: 1.6;
        margin-bottom: 2rem;
        max-width: 500px;
        margin-left: auto;
        margin-right: auto;
      }

      .new-request-button {
        background: #ff6b00;
        color: white;
        border: none;
        padding: 1rem 2rem;
        border-radius: 10px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        transition: all 0.3s ease;
        font-size: 1rem;

        &:hover {
          background: #e66000;
          transform: scale(1.05);
        }
      }
    }
  }

  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 0.3s ease;

    .modal-container {
      transition: transform 0.3s ease;
    }
  }

  .modal-fade-enter-from,
  .modal-fade-leave-to {
    opacity: 0;

    .modal-container {
      transform: scale(0.9);
    }
  }
}

@media (max-width: 768px) {
  .car-request-wrapper {
    .modal-container {
      max-height: 95vh;
    }

    .car-request-form {
      padding: 1.5rem;

      .form-header {
        padding-right: 1rem;

        i {
          font-size: 2.5rem;
        }

        h2 {
          font-size: 1.8rem;
        }

        .subtitle {
          font-size: 0.9rem;
        }
      }

      .form-grid {
        grid-template-columns: 1fr;
        gap: 1.2rem;
      }
    }

    .close-button {
      top: 1rem;
      right: 1rem;
      width: 36px;
      height: 36px;
    }
  }
}

@media (max-width: 480px) {
  .car-request-wrapper {
    .open-modal-button {
      padding: 0.9rem 1.5rem;
      font-size: 1rem;
    }

    .modal-overlay {
      padding: 0.5rem;
    }

    .car-request-form {
      padding: 1rem;

      .form-header {
        margin-bottom: 1.5rem;

        i {
          font-size: 2rem;
        }

        h2 {
          font-size: 1.5rem;
        }
      }

      .submit-button {
        padding: 1rem;
        font-size: 1rem;
      }

      .success-section {
        .success-icon i {
          font-size: 4rem;
        }

        h3 {
          font-size: 1.6rem;
        }

        p {
          font-size: 1rem;
        }
      }
    }
  }
}
</style>
